import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Turnstile from '../ui/Turnstile';

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin }) => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  
  const { register, loading, error, clearError } = useAuth();

  const validateStep1 = () => {
    if (!email) {
      setFormError('Email is required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setFormError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!username) {
      setFormError('Username is required');
      return false;
    }
    if (username.length < 3) {
      setFormError('Username must be at least 3 characters');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!password) {
      setFormError('Password is required');
      return false;
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return false;
    }
    if (!turnstileToken) {
      setFormError('Please complete the security verification');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    setFormError(null);
    
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handlePrevStep = () => {
    setFormError(null);
    setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormError(null);
    
    if (validateStep3()) {
      await register(email, username, password, displayName || undefined, turnstileToken);
    }
  };

  return (
    <div>
      {(error || formError) && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-xl text-red-200 text-sm">
          {error || formError}
        </div>
      )}
      
      <div className="mb-4 flex justify-between">
        <div className={`w-1/3 h-1 rounded-full mr-1 ${step >= 1 ? 'bg-[#FF4D8C]' : 'bg-gray-700'}`}></div>
        <div className={`w-1/3 h-1 rounded-full mx-1 ${step >= 2 ? 'bg-[#FF4D8C]' : 'bg-gray-700'}`}></div>
        <div className={`w-1/3 h-1 rounded-full ml-1 ${step >= 3 ? 'bg-[#FF4D8C]' : 'bg-gray-700'}`}></div>
      </div>
      
      <form onChange={() => { clearError(); setFormError(null); }}>
        {step === 1 && (
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-white mb-2">Your Email</h4>
            <div>
              <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-300">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D8C] focus:border-transparent transition-all duration-200"
                placeholder="your@email.com"
              />
              <p className="mt-1 text-xs text-gray-400">We'll send a verification link to this address</p>
            </div>
          </div>
        )}
        
        {step === 2 && (
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-white mb-2">Your Identity</h4>
            <div>
              <label htmlFor="username" className="block mb-2 text-sm font-medium text-gray-300">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D8C] focus:border-transparent transition-all duration-200"
                placeholder="cooluser123"
              />
              <p className="mt-1 text-xs text-gray-400">This will be your unique identifier</p>
            </div>
            
            <div>
              <label htmlFor="displayName" className="block mb-2 text-sm font-medium text-gray-300">
                Display Name (optional)
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D8C] focus:border-transparent transition-all duration-200"
                placeholder="Cool User"
              />
              <p className="mt-1 text-xs text-gray-400">How others will see you</p>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div className="space-y-4">
            <h4 className="text-lg font-medium text-white mb-2">Secure Your Account</h4>
            <div>
              <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-300">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D8C] focus:border-transparent transition-all duration-200"
                placeholder="••••••••"
              />
              <p className="mt-1 text-xs text-gray-400">Minimum 8 characters</p>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-300">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-gray-900 border border-gray-700 text-white text-sm rounded-xl w-full p-3 focus:outline-none focus:ring-2 focus:ring-[#FF4D8C] focus:border-transparent transition-all duration-200"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-300">
                Security Verification
              </label>
              <Turnstile
                siteKey="0x4AAAAAABkVLUQioNOPzqGJ"
                onVerify={setTurnstileToken}
                onError={(error) => setFormError(`Security verification failed: ${error}`)}
                onExpire={() => setTurnstileToken('')}
                theme="dark"
                className="mb-2"
              />
              <p className="mt-1 text-xs text-gray-400">Complete this verification to prevent spam</p>
            </div>
          </div>
        )}
        
        <div className="mt-6 flex justify-between">
          {step > 1 ? (
            <button
              type="button"
              onClick={handlePrevStep}
              className="px-4 py-2 border border-gray-700 text-gray-300 rounded-xl hover:bg-gray-800 transition-colors"
            >
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Already have an account?
            </button>
          )}
          
          {step < 3 ? (
            <button
              type="button"
              onClick={handleNextStep}
              className="px-6 py-2 bg-[#FF4D8C] hover:bg-[#FF6E9E] text-white rounded-xl transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-[#FF4D8C] hover:bg-[#FF6E9E] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;
